import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';

export default function AboutPage() {
  const navigate = useNavigate();
  const { user } = useApp();

  const handleCTA = () => {
    if (user) navigate('/');
    else navigate('/?signup=true');
  };

  const sectionStyle = {
    maxWidth: 720, margin: '0 auto', padding: '72px 24px',
  };

  const pillStyle = {
    display: 'inline-block', fontSize: 10,
    letterSpacing: '0.15em', textTransform: 'uppercase',
    color: '#D4537E', border: '0.5px solid rgba(212,83,126,0.4)',
    borderRadius: 20, padding: '5px 14px', marginBottom: 20,
  };

  const featureCardStyle = {
    background: 'rgba(255,255,255,0.03)',
    border: '0.5px solid rgba(212,83,126,0.15)',
    borderRadius: 16, padding: 24,
  };

  const dividerStyle = {
    height: '0.5px',
    background: 'linear-gradient(90deg, transparent, rgba(212,83,126,0.2), transparent)',
    margin: '0 24px',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#080608', color: '#f5e6ed', position: 'relative', overflow: 'hidden' }}>

      {/* Grain */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, opacity: 0.25,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        backgroundSize: '180px',
      }} />

      {/* Glow */}
      <div style={{
        position: 'fixed', top: '-20%', left: '50%', transform: 'translateX(-50%)',
        width: 600, height: 600, pointerEvents: 'none', zIndex: 0,
        background: 'radial-gradient(circle, rgba(212,83,126,0.06) 0%, transparent 70%)',
      }} />

      {/* Nav */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(8,6,8,0.92)', backdropFilter: 'blur(20px)',
        borderBottom: '0.5px solid rgba(212,83,126,0.15)', padding: '0 24px',
      }}>
        <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 62 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #D4537E, #993556)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#fff' }}>♪</div>
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 18, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#f5e6ed' }}>Echo Moments</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => navigate('/')} style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(245,230,237,0.45)', background: 'none', border: '0.5px solid rgba(212,83,126,0.25)', borderRadius: 20, padding: '6px 16px', cursor: 'pointer' }}>
              {user ? 'Go to feed' : 'Sign in'}
            </button>
            {!user && (
              <button onClick={handleCTA} style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#fff', background: '#D4537E', border: 'none', borderRadius: 20, padding: '6px 16px', cursor: 'pointer' }}>
                Join free
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <div style={{ ...sectionStyle, textAlign: 'center', paddingTop: 96, paddingBottom: 72, position: 'relative', zIndex: 1 }}>
        <div style={pillStyle}>✦ Now with Echo Room — listen together</div>
        <h1 style={{
          fontFamily: "'Cormorant Garamond', serif", fontWeight: 300,
          fontSize: 'clamp(36px, 6vw, 58px)', lineHeight: 1.1,
          letterSpacing: '0.02em', marginBottom: 20, color: '#f5e6ed',
        }}>
          That one part of the song<br />
          <em style={{ fontStyle: 'italic', color: '#D4537E' }}>everyone needs to hear.</em>
        </h1>
        <p style={{ fontSize: 15, color: 'rgba(245,230,237,0.5)', maxWidth: 480, margin: '0 auto 36px', lineHeight: 1.8 }}>
          Share your favourite song moments. React with friends. Listen together in real-time. EchoMoments is where music feels personal again.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={handleCTA} style={{ fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#fff', background: '#D4537E', border: 'none', borderRadius: 20, padding: '12px 28px', cursor: 'pointer' }}>
            Start sharing moments
          </button>
          <button onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })} style={{ fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(245,230,237,0.6)', background: 'transparent', border: '0.5px solid rgba(212,83,126,0.3)', borderRadius: 20, padding: '12px 28px', cursor: 'pointer' }}>
            See how it works ↓
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 32, justifyContent: 'center', marginTop: 56, flexWrap: 'wrap' }}>
          {[
            { value: '∞', label: 'Moments shared' },
            { value: '🔥', label: 'Live reactions' },
            { value: '🎧', label: 'Echo Rooms' },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 300, color: '#D4537E' }}>{s.value}</div>
              <div style={{ fontSize: 10, color: 'rgba(245,230,237,0.3)', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={dividerStyle} />

      {/* ── FEATURES ── */}
      <div style={{ ...sectionStyle, position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={pillStyle}>Features</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 'clamp(28px, 4vw, 40px)', letterSpacing: '0.02em', color: '#f5e6ed' }}>
            Everything music social<br />
            <em style={{ color: '#D4537E' }}>should be.</em>
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          {[
            { emoji: '🎵', title: 'Share moments', desc: 'Pin the exact second that gives you chills. Set start and end points. Share it with the world.' },
            { emoji: '🔥', title: 'React & vibe', desc: '8 reactions, live comments, real emotions. No likes — just vibes.' },
            { emoji: '🏷️', title: 'Tags & vibes', desc: '#NightDrive #Heartbreak #GoodVibes — tag your moments and discover others like you.' },
            { emoji: '🎶', title: 'Collections', desc: 'Curate playlists of moments. Shuffle, auto-play, share with anyone.' },
          ].map((f, i) => (
            <div key={i} style={featureCardStyle}>
              <div style={{ fontSize: 24, marginBottom: 12 }}>{f.emoji}</div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 400, marginBottom: 8, color: '#f5e6ed' }}>{f.title}</div>
              <div style={{ fontSize: 13, color: 'rgba(245,230,237,0.4)', lineHeight: 1.6 }}>{f.desc}</div>
            </div>
          ))}
        </div>

        {/* Echo Room featured card */}
        <div style={{ marginTop: 16, background: 'rgba(212,83,126,0.05)', border: '0.5px solid rgba(212,83,126,0.35)', borderRadius: 16, padding: 28, display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#D4537E', marginBottom: 10 }}>✦ Signature feature</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 400, marginBottom: 8, color: '#f5e6ed' }}>🎧 Echo Room</div>
            <div style={{ fontSize: 13, color: 'rgba(245,230,237,0.5)', lineHeight: 1.7 }}>
              Create a room, share the link, and listen to the exact same moment at the exact same time — with live reactions and chat. Music is better together.
            </div>
          </div>
          {/* Mini room mockup */}
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '0.5px solid rgba(212,83,126,0.2)', borderRadius: 12, padding: 16, minWidth: 180 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e' }} />
              <span style={{ fontSize: 10, color: 'rgba(245,230,237,0.4)', letterSpacing: '0.08em' }}>LIVE · 3 listening</span>
            </div>
            <div style={{ background: 'rgba(212,83,126,0.1)', borderRadius: 8, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 10 }}>♪</div>
            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 2, color: '#f5e6ed' }}>Maan Meri Jaan</div>
            <div style={{ fontSize: 10, color: 'rgba(245,230,237,0.35)', marginBottom: 10 }}>King · from 1:02</div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>⏮</div>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#D4537E', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>⏸</div>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>⏭</div>
            </div>
          </div>
        </div>
      </div>

      <div style={dividerStyle} />

      {/* ── HOW IT WORKS ── */}
      <div id="how-it-works" style={{ ...sectionStyle, position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={pillStyle}>How it works</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 'clamp(28px, 4vw, 40px)', letterSpacing: '0.02em', color: '#f5e6ed' }}>
            Three steps to<br />
            <em style={{ color: '#D4537E' }}>your moment.</em>
          </h2>
        </div>
        {[
          { num: '01', title: 'Find your song', desc: 'Search any song on YouTube. Play it right inside EchoMoments with our built-in player.' },
          { num: '02', title: 'Pin the moment', desc: 'Drag to the exact second that hits different. Set a start and end point. Add a note about why it matters.' },
          { num: '03', title: 'Share & connect', desc: 'Post to the global feed. Invite friends to an Echo Room. Let the music do the talking.' },
        ].map((s, i) => (
          <div key={i} style={{ display: 'flex', gap: 24, alignItems: 'flex-start', padding: '28px 0', borderBottom: i < 2 ? '0.5px solid rgba(212,83,126,0.1)' : 'none' }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 52, fontWeight: 300, color: 'rgba(212,83,126,0.2)', lineHeight: 1, flexShrink: 0 }}>{s.num}</div>
            <div style={{ paddingTop: 8 }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 400, marginBottom: 8, color: '#f5e6ed' }}>{s.title}</div>
              <div style={{ fontSize: 14, color: 'rgba(245,230,237,0.45)', lineHeight: 1.7 }}>{s.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={dividerStyle} />

      {/* ── CTA ── */}
      <div style={{ ...sectionStyle, textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 'clamp(32px, 5vw, 48px)', letterSpacing: '0.02em', marginBottom: 16, color: '#f5e6ed' }}>
          Ready to share<br />
          <em style={{ color: '#D4537E' }}>your moment?</em>
        </h2>
        <p style={{ fontSize: 14, color: 'rgba(245,230,237,0.4)', marginBottom: 36, lineHeight: 1.7 }}>
          Free forever. No ads. Just music.
        </p>
        <button onClick={handleCTA} style={{ fontSize: 14, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#fff', background: '#D4537E', border: 'none', borderRadius: 20, padding: '14px 36px', cursor: 'pointer', boxShadow: '0 0 32px rgba(212,83,126,0.25)' }}>
          {user ? 'Go to feed ♪' : 'Join EchoMoments — it\'s free'}
        </button>
      </div>
//
      {/* Footer */}
      <footer style={{ borderTop: '0.5px solid rgba(212,83,126,0.1)', padding: '24px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', gap: 24, justifyContent: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          <span onClick={() => navigate('/')} style={{ fontSize: 11, color: 'rgba(245,230,237,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer' }}>Feed</span>
          <span onClick={() => navigate('/collections')} style={{ fontSize: 11, color: 'rgba(245,230,237,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer' }}>Collections</span>
          <span style={{ fontSize: 11, color: 'rgba(245,230,237,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Privacy Policy</span>
          <span style={{ fontSize: 11, color: 'rgba(245,230,237,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Terms</span>
        </div>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 13, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#D4537E', opacity: 0.7 }}>
          ♪ Made by Shubh
        </p>
      </footer>
    </div>
  );
}